import { router, useForm } from "@inertiajs/react"
import { Users } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Empty } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatRelativeTime } from "@/lib/utils"
import {
  inviteUserPartnerAccountPath,
  resendUserInvitePartnerAccountPath,
  revokeUserPartnerAccountPath,
} from "@/routes"

import type { AccountUser } from "./types"

// IMP-404: a partner can bootstrap the account's first user from here, and
// resend/revoke a pending invite — but only while the account has no active
// (accepted) user. `canInvite` comes from the server (account.can_invite_user);
// the real enforcement is the server-side guard, this just hides the controls.
export function UsersTab({
  users,
  accountUid,
  canInvite,
}: {
  users: AccountUser[]
  accountUid: string
  canInvite: boolean
}) {
  const [addOpen, setAddOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/unbound-method -- Inertia useForm returns closures
  const { data, setData, post, processing, reset, errors } = useForm({
    email: "",
  })

  function handleInvite() {
    post(inviteUserPartnerAccountPath({ id: accountUid }), {
      preserveScroll: true,
      onSuccess: () => {
        setAddOpen(false)
        reset()
      },
    })
  }

  function resend(userUid: string) {
    router.post(
      resendUserInvitePartnerAccountPath({ id: accountUid }),
      { user_uid: userUid },
      { preserveScroll: true },
    )
  }

  function revoke(userUid: string) {
    if (!confirm("Revoke this invitation?")) return
    router.delete(revokeUserPartnerAccountPath({ id: accountUid }), {
      data: { user_uid: userUid },
      preserveScroll: true,
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Users</h2>
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          disabled={!canInvite}
          title={
            canInvite
              ? undefined
              : "The account owner manages users once someone has accepted access."
          }
          onClick={() => setAddOpen(true)}
        >
          Add user
        </Button>
      </div>

      {users.length === 0 ? (
        <Empty
          icon={<Users className="h-5 w-5" />}
          title="No users"
          description="No users have been added to this account yet."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="text-foreground min-w-full text-left text-sm/6">
            <thead className="text-muted-foreground">
              <tr>
                <th className="border-b px-4 py-2 font-medium first:pl-1 last:pr-1">
                  Name
                </th>
                <th className="border-b px-4 py-2 font-medium first:pl-1 last:pr-1">
                  Role
                </th>
                <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                  Last active
                </th>
                <th className="border-b px-4 py-2 first:pl-1 last:pr-1" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.uid} className="hover:bg-muted/50">
                  <td className="border-b px-4 py-4 first:pl-1 last:pr-1">
                    <div className="flex items-center gap-2 font-medium">
                      {user.name}
                      {user.pending && (
                        <Badge variant="warning" className="font-normal">
                          Pending
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-sm">
                      {user.email}
                    </div>
                  </td>
                  <td className="text-muted-foreground border-b px-4 py-4 capitalize first:pl-1 last:pr-1">
                    {user.role}
                  </td>
                  <td className="text-muted-foreground border-b px-4 py-4 text-right first:pl-1 last:pr-1">
                    {formatRelativeTime(user.last_sign_in_at ?? undefined)}
                  </td>
                  <td className="border-b px-4 py-4 text-right first:pl-1 last:pr-1">
                    {/* Resend/revoke only for a pending invite, and only while the
                        partner may still manage access. */}
                    {canInvite && user.pending && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-sm"
                          onClick={() => resend(user.uid)}
                        >
                          Resend
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive rounded-sm"
                          onClick={() => revoke(user.uid)}
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open)
          if (!open) reset()
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="example@company.com"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-destructive text-xs">{errors.email}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-sm"
              disabled={!data.email.trim() || processing}
              loading={processing}
              onClick={handleInvite}
            >
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
